<template>
  <div class="main-page">
    <center-spinner :loading="loading" />
    <div class="auth-content2">
      <h1
        v-if="checkOrganization"
        class="header-style"
      >
        {{ organization.name }}
      </h1>
      <h1
        v-else
        class="header-style"
      >
        Set Passcode
      </h1>
      <br>
      <div
        v-if="verificationFailed"
        class="fail-message-style mb-4"
      >
        <p class="fail-style">
          <v-icon
            color="#C62828"
            size="30"
          >
            mdi-alert
          </v-icon> <br>
          {{ verificationFailedMessage }}
        </p>
      </div>
      <p class="subheader-style">
        Please confirm the details below and set your login passcode.
      </p>
      <v-form
        ref="form"
        v-model="valid"
      >
        <v-text-field
          v-model="formData.firstName"
          class="input-style"
          label="First Name*"
          outlined
          :rules="[(v) => !!v || 'This field is required']"
          color="#000000"
          dense
          filled
          light
        />
        <v-text-field
          v-model="formData.lastName"
          class="input-style"
          label="Last Name*"
          outlined
          :rules="[(v) => !!v || 'This field is required']"
          color="#000000"
          dense
          filled
          light
        />
        <v-text-field
          v-model="formData.email"
          class="input-style"
          label="Email Address"
          outlined
          color="#000000"
          dense
          filled
          light
        />
        <v-text-field
          v-model="formData.phoneNumber"
          class="input-style"
          label="Mobile Number*"
          outlined
          color="#000000"
          type="number"
          inputmode="numeric"
          dense
          filled
          disabled
          light
        />
        <v-select
          v-if="!checkUserDetails && checkOrganization"
          v-model="formData.company"
          light
          color="#37474F"
          flat
          dense
          :items="projectCompanies"
          item-text="name"
          item-value="id"
          label="Choose a Company*"
          outlined
          background-color="transparent"
          class="small-select"
          :rules="
            !checkUserDetails ? [(v) => !!v || 'This field is required'] : []
          "
        />
        <br>
        <v-btn
          color="#37474F"
          class="emergency-style"
          small
          rounded
          elevation="0"
          @click="emergencyContact()"
        >
          <v-icon small>
            mdi-plus
          </v-icon>
          <font>Emergency Contact</font>
        </v-btn>
        <br>
        <br>
        <div
          v-for="item in emergencyContacts"
          :key="item.name"
        >
          <v-card
            light
            elevation="0"
            class="card-style"
          >
            <v-card-text>
              <p class="title-text-style">
                Name:
                <font class="value-text-style">
                  {{ item.name }}
                </font>
              </p>
              <p class="title-text-style">
                Phone Number:
                <font class="value-text-style">
                  {{ item.phone_number }}
                </font>
              </p>
              <p class="title-text-style">
                Relation:
                <font class="value-text-style">
                  {{ item.relation }}
                </font>
              </p>
            </v-card-text>
          </v-card>
        </div>
        <br>
        <div class="otp-div">
          <p class="passcode-title">
            Set Passcode
          </p>
          <div class="otp-style">
            <v-otp-input
              v-model="formData.passcode"
              type="number"
              :rules="[(v) => !!v || 'This field is required']"
              length="4"
              inputmode="numeric"
              light
              color="#000000"
            />
          </div>
          <p class="passcode-title">
            Confirm Passcode
          </p>
          <p
            v-if="isPasscodeMatch"
            class="passcode-mismatch"
          >
            {{ passwordConfirmationRule }}
          </p>
          <div class="otp-style">
            <v-otp-input
              v-model="formData.confirmPasscode"
              type="number"
              length="4"
              inputmode="numeric"
              light
              :rules="confirmPasswordRules.concat(passwordConfirmationRule)"
              color="#000000"
            />
          </div>
        </div>
        <v-btn
          class="mt-2 btn-style"
          color="#37474F"
          elevation="0"
          @click="completeRegistration()"
        >
          Register
        </v-btn>
        <v-btn
          light
          color="#E6E6E6"
          class="mt-3 btn-style"
          elevation="0"
          @click="cancelRegister()"
        >
          Cancel
        </v-btn>
      </v-form>
    </div>
    <add-emergency-contact
      v-if="showEmergencyContact"
      @closed="showEmergencyContact = false"
    />
  </div>
</template>

<script>
import AddEmergencyContact from './AddEmergencyContact.vue';
import spinner from '../../components/SpinnerCentre.vue';
import Constants from 'src/constants';

export default {
  name: 'ReviewDetails',
  components: {
    'center-spinner': spinner,
    'add-emergency-contact': AddEmergencyContact,
  },
  data: () => ({
    loading: false,
    valid: false,
    formData: {
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: null,
      passcode: '',
      confirmPasscode: '',
      company: null,
    },
    confirmPasswordRules: [(v) => !!v || 'This field is required'],
    verificationFailed: false,
    verificationFailedMessage: '',
    showEmergencyContact: false,
    organization: null,
    countryCode: '',
  }),
  computed: {
    userDetails () {
      return this.$store.getters['register/getUserDetails'];
    },
    passwordConfirmationRule () {
      if (this.formData.passcode === this.formData.confirmPasscode) {
        return true;
      }
      return 'Passcode must match';
    },
    isPasscodeMatch () {
      return (
        this.formData.confirmPasscode !== '' &&
        this.passwordConfirmationRule !== true
      );
    },
    emergencyContacts () {
      return this.$store.getters['register/getEmergencyContacts'];
    },
    checkOrganization () {
      if (this.organization === null) {
        return false;
      }
      return true;
    },
    checkUserDetails () {
      if (Object.keys(this.userDetails).length !== 0) {
        return true;
      }
      return false;
    },
    projectCompanies () {
      return this.$store.getters['projects/getProjectCompanies'];
    },
  },
  async mounted () {
    this.loading = true;
    await this.fillData();
    await this.setOrgDetails();
    await this.fetchProjectCompanies();
    this.loading = false;
  },
  methods: {
    async fillData () {
      this.formData.firstName = this.userDetails.first_name;
      this.formData.lastName = this.userDetails.last_name;
      this.formData.email = this.userDetails.email;
      this.formData.phoneNumber = this.userDetails.phone_number;

      const phoneNumberData = localStorage.getItem('ph_number');
      const phoneNumber = JSON.parse(phoneNumberData);
      if (phoneNumberData && !this.checkUserDetails) {
        this.formData.phoneNumber = phoneNumber.phone_number;
      }
      this.countryCode = phoneNumber.country_code;
    },
    async setOrgDetails () {
      const orgData = JSON.parse(localStorage.getItem('orgDetails'));
      if (orgData) {
        this.organization = orgData;
      }
    },
    async fetchProjectCompanies () {
      const projectGateId = localStorage.getItem('siteId');
      if (projectGateId) {
        await this.$store.dispatch('projects/fetchProjectCompanies', {
          id: projectGateId,
        });
      }
    },
    async completeRegistration () {
      if (this.$refs.form.validate() === false) {
        this.$store.dispatch(
          'alert/onAlert',
          {
            message: 'Please fill the required fields before proceeding',
            type: Constants.ALERT_TYPE_ERROR,
          },
          { root: true },
        );
        return;
      }
      this.loading = true;
      await this.$store
        .dispatch('register/registerAppUser', {
          details: {
            organization_id:
              this.organization !== null ? this.organization.external_id : null,
            first_name: this.formData.firstName,
            last_name: this.formData.lastName,
            email: this.formData.email,
            password: this.formData.passcode,
            company_id: this.formData.company,
            phone_number: this.formData.phoneNumber,
            country_code: this.countryCode,
            password_confirmed: this.passwordConfirmationRule,
            emergency_contact: this.emergencyContacts,
          },
        })
        .then((response) => {
          localStorage.removeItem('orgDetails');
          localStorage.removeItem('ph_number');
          this.$router.push({ name: 'RegistrationSuccess' });
        })
        .catch((error) => {
          this.loading = false;
          this.$store.dispatch(
            'alert/onAlert',
            {
              message: error.response.data.message,
              type: Constants.ALERT_TYPE_ERROR,
            },
            { root: true },
          );
          this.verificationFailed = true;
          this.verificationFailedMessage = error.response.data.message;
        });
    },
    emergencyContact () {
      if (this.emergencyContacts.length === 2) {
        this.$store.dispatch(
          'alert/onAlert',
          {
            message: 'The user is limited to adding two contact details.',
            type: Constants.ALERT_TYPE_ERROR,
          },
          { root: true },
        );
        return;
      }
      this.showEmergencyContact = true;
    },
    cancelRegister () {
      this.loading = true;
      localStorage.removeItem('ph_number');
      setTimeout(() => {
        this.$router.push({ name: 'Register' });
      }, 1000);
    },
  },
};
</script>
<style scoped>
.emergency-style {
  text-transform: none !important;
}
.card-style {
  border-bottom: 1px solid #e6e6e6;
}
.theme--dark.v-list {
  background-color: white !important;
}
.v-input--selection-controls__input .v-icon {
  color: #ccc !important;
}
.theme--dark >>> .v-list-item {
  color: #37474f !important;
}
.theme--dark.v-label {
  color: rgba(255, 255, 255, 0.7);
}
.theme--dark.v-messages {
  color: #37474f !important;
}
.hint-text {
  font-size: 13px;
  color: #444444;
  margin-top: -13px !important;
}
.class-disabled .v-input--selection-controls__input .v-icon {
  color: #000 !important;
}
.disabled-class {
  color: #ccc !important;
}
.checkin .mdi-checkbox-marked::before {
  color: #0000 !important;
  caret-color: #0000 !important;
}

::v-deep .v-input--selection-controls__input .v-icon {
  color: rgb(8, 8, 8) !important;
}
</style>
